/**
 * Screenplay-blocking feature module (Task 5).
 *
 * Inbound: `castCatalog` — Unity pushes the LIVE bundled-Avaturn roster (already filtered to
 * pool-materializable entries; picker-only bases like IAN are excluded at the source). The handler maps
 * the 4-field wire members into the kernel's `CastMember` shape and parks them in `useBlockingStore` for
 * the casting layer (`buildCastIndex`). `file` and `look` are set to minimal placeholders — the resolver
 * (`resolveCastForCharacter`) reads only bundledId/gender/baseModelId/displayName.
 *
 * Outbound: the web layer computes the screenplay→coverage→rough-block plan and forwards results to
 * Unity for persistence / scene materialization via three fire-and-forget senders. Web→Unity transport is
 * `JSON.stringify({ action, ...payload })`, so each sender spreads its payload at the top level next to
 * `action`.
 *
 * Modeled on features/screenplay.ts.
 */

import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative, diagLog } from '../transport';
import type { CastMember, AvaturnLook } from '@/staging/casting.ts';
import { useBlockingStore } from '@/store/blockingStore';
import { useProjectStore } from '@/store/projectStore';
import { useUiStore } from '@/store/uiStore';
import { capabilities } from '@/capabilities';

// ─── Inbound: castCatalog ─────────────────────────────────────────────────

interface CastCatalogWireMember {
  bundledId: string;
  gender: string;
  baseModelId: string;
  displayName: string;
}

export interface CastCatalogMessage {
  type: 'castCatalog';
  payload: { members: CastCatalogWireMember[] };
}

export interface CoverageAppliedMessage {
  type: 'coverageApplied';
  payload: { sceneId: number; shotIds: number[]; ok: boolean };
}

// 20260909 (owner) — BLOCK SHOTS OVERLAY. Unity acks the rough-block run's LAST send (the one that
// carries `repush:true`), never the per-shot ones: the run is fire-and-forget and the web arms exactly
// one waiter per run. `fillPending` is Unity telling us a scoped board fill is walking THIS scene, so
// the tiles are still being re-rendered and the ack alone is too early to drop the blocker — see
// SceneBoardExpanded's hold, which then waits for that fill's terminal snapshot.
//
// 20260909 review fix — `sceneId` is not just diagnostics: it is what CORRELATES an ack with the run
// that armed the waiter. Nothing disarms a timed-out run's Deferred, so a late ack from a run the hold
// already gave up on would otherwise resolve the NEXT run's waiter and drop that run's blocker on the
// wrong `fillPending`. `ok:false` means Unity rejected the payload (its own validation), not that the
// scene is unknown.
export interface RoughBlockAppliedMessage {
  type: 'roughBlockApplied';
  payload: { sceneId: number; shotId: number; ok: boolean; fillPending: boolean };
}

// 20260909 - CAST PHASE (owner batch 11, item 5). Unity's board fill asks for a cast-pipeline run and
// holds its poster pass until we answer `castPipelineDone`. Route-independent BY DESIGN: the board
// effect that used to be the pipeline's only trigger does not exist on the Script tab, which is exactly
// how an imported script reached the board with preset stand-in bodies. Payload is empty.
export interface RequestCastPipelineMessage {
  type: 'requestCastPipeline';
  payload: Record<string, never>;
}

export type BlockingMessage = CastCatalogMessage | CoverageAppliedMessage | RoughBlockAppliedMessage | RequestCastPipelineMessage;

/** Type-complete but inert `look`: `resolveCastForCharacter` never reads it (nor `file`), but `CastMember`
 *  requires an `AvaturnLook`, so satisfy it minimally rather than casting the member shape. */
function placeholderLook(gender: 'male' | 'female', baseModelId: string): AvaturnLook {
  return {
    gender,
    baseModelId,
    assets: { hair: null, clothing: null, eyewear: null, footwear: null },
    hairColor: '',
    eyeColor: '',
    skinTone: 0,
  };
}

export const blockingHandlers = {
  castCatalog: (msg: CastCatalogMessage) => {
    const members: CastMember[] = (msg.payload?.members ?? []).map((m) => {
      const gender: 'male' | 'female' = m.gender?.toLowerCase() === 'female' ? 'female' : 'male';
      return {
        bundledId: m.bundledId,
        gender,
        displayName: m.displayName,
        baseModelId: m.baseModelId,
        file: '', // unused by resolveCastForCharacter
        look: placeholderLook(gender, m.baseModelId), // unused by resolveCastForCharacter
      };
    });
    useBlockingStore.getState().setCastCatalog(members);
  },
  coverageApplied: (msg: CoverageAppliedMessage) => {
    const p = msg.payload;
    coverageAppliedWaiter?.resolve({
      sceneId: typeof p?.sceneId === 'number' ? p.sceneId : 0,
      shotIds: Array.isArray(p?.shotIds) ? p.shotIds : [],
      ok: !!p?.ok,
    });
  },
  // 20260909 (owner) — BLOCK SHOTS OVERLAY. Null-safe like its coverageApplied sibling and for a
  // stronger reason: this ack can arrive with NO armed waiter at all (a cancel's flush, a merge-path
  // rough block, a second run whose hold already released on the 30 s cap), and a stray ack must never
  // throw out of the dispatch table. `?.` on the waiter is the whole guard.
  //
  // 20260909 review fix — and it must be the RIGHT run's ack. The waiter is armed before
  // applySceneSelection, whose 'background' branch runs an uncapped AI image generation after the
  // rough block, so the armed window can span a minute; a run that timed out leaves its Deferred
  // armed-but-abandoned, and the late ack that finally arrives would resolve whatever waiter is armed
  // NOW — dropping a second scene's blocker early, on the first scene's `fillPending`. So resolve only
  // for the scene this waiter was armed for. `sceneId` 0 is the one exception: it means Unity could not
  // parse a scene out of the payload at all (that ack is ok:false), and it must still release the hold
  // rather than strand the overlay for the full cap.
  roughBlockApplied: (msg: RoughBlockAppliedMessage) => {
    const p = msg.payload;
    // NaN folds into the same 0 bucket as a missing field: an unusable scene id must RELEASE the hold,
    // never fail the match below and strand the overlay for the full cap.
    const sceneId = typeof p?.sceneId === 'number' && Number.isFinite(p.sceneId) ? p.sceneId : 0;
    const armed = roughBlockAppliedWaiter;
    if (armed && sceneId !== 0 && sceneId !== armed.sceneId) {
      diagLog('RoughBlock', `roughBlockApplied for scene ${sceneId} IGNORED — the armed waiter belongs to scene ${armed.sceneId} (late ack from a timed-out run)`);
      return;
    }
    armed?.deferred.resolve({
      sceneId,
      shotId: typeof p?.shotId === 'number' ? p.shotId : 0,
      ok: !!p?.ok,
      fillPending: !!p?.fillPending,
    });
  },
  // 20260909 - CAST PHASE (owner batch 11, item 5). The gates are the web trigger's, verbatim
  // (20260930: useCastPipelineTrigger, called from SceneBoardPage on every route — Script, Timeline and
  // the compact board cast too; it no longer lives in SceneBoardExpanded): the pipeline writes cast rows through Unity, so it must never run
  // for a review session or a build with the feature off. When gated OFF we answer IMMEDIATELY rather
  // than staying silent — silence would park Unity's fill for the whole 60 s safety cap on every open
  // of a project the pipeline was never going to touch.
  //
  // Dynamic import for the same reason useCastPipelineTrigger uses one: dossierPipeline imports this module's
  // senders, so a static import would close a cycle.
  //
  // The done is sent HERE, from the request that asked for it — never from inside the pipeline (review
  // fix, 20260909). The pipeline is also invoked per project identity by useCastPipelineTrigger, and the
  // fill's drain writes the store once per completed scene, so a send in the pipeline's own `.finally`
  // answered once per store write: ~193 bridge messages and ~193 latch flips on a feature-length fill.
  // Chaining it here keeps every property the phase needs: one done per REQUEST, on every path (a bail,
  // a throw and a chunk that never loaded all reach `.finally`), and — because a caller arriving mid-run
  // joins the in-flight promise — settled only after that run's setCharacterDossiers messages are already
  // enqueued ahead of it on the same transport.
  requestCastPipeline: () => {
    const screenplayBlocking = useUiStore.getState().screenplayBlocking;
    const canEdit = capabilities.bridge && !useProjectStore.getState().readOnly;
    if (!screenplayBlocking || !canEdit) {
      diagLog('CastTrace', `requestCastPipeline declined: screenplayBlocking=${screenplayBlocking} canEdit=${canEdit} — answering done immediately`);
      sendCastPipelineDone(0);
      return;
    }
    diagLog('CastTrace', 'requestCastPipeline: Unity asked for a cast pipeline run (board fill is holding)');
    let sentUpdates = 0;
    void import('@/integration/dossierPipeline.ts')
      .then((m) => m.ensureDossiersAndCasting())
      .then((sent) => { sentUpdates = sent; })
      .catch((err) => {
        diagLog('CastTrace', `requestCastPipeline: pipeline run FAILED (${String(err)}) — answering done so the fill is not held`);
      })
      .finally(() => {
        diagLog('CastTrace', `requestCastPipeline: run settled — answering castPipelineDone (${sentUpdates} update(s) sent)`);
        sendCastPipelineDone(sentUpdates);
      });
  },
} satisfies HandlerMap;

// ─── Outbound senders ─────────────────────────────────────────────────────

export function sendSetCharacterDossiers(
  characters: { characterId: number; appearanceDesc: string; castBundledId: string }[],
): void {
  enqueueSendToNative('setCharacterDossiers', { characters });
}

export function sendApplyCoverage(payload: ApplyCoveragePayload): void {
  enqueueSendToNative('applyCoverage', { ...payload });
}

export function sendApplyRoughBlock(payload: ApplyRoughBlockPayload): void {
  enqueueSendToNative('applyRoughBlock', { ...payload });
}

// 20260707 - SCREENPLAY BLOCKING: persist the scene's SuggestedShotsEnvelope (JSON.stringify'd by the
// caller) so re-opening the Coverage Review dialog can skip the LLM call. Flat wire shape like the
// sibling senders: { action:'setSuggestedShots', sceneId, suggestedShots }.
export function sendSetSuggestedShots(sceneId: number, suggestedShots: string): void {
  enqueueSendToNative('setSuggestedShots', { sceneId, suggestedShots });
}

// 20260909 - CAST PHASE (owner batch 11, item 5): "the run you asked for is over". Unity's board fill
// parks its poster pass on this latch, so it must go out on EVERY path of a requested run, bails and
// throws included — see the `requestCastPipeline` handler, which owns the one send per request. Same
// enqueue as the sibling senders, so it is ordered AFTER every setCharacterDossiers the run emitted:
// when Unity reads the latch, the bodies are already applied. `sent` is a diagnostic; Unity ignores it.
export function sendCastPipelineDone(sent: number): void {
  enqueueSendToNative('castPipelineDone', { sent });
}

// ─── Outbound payload interfaces (consumed by Tasks 6–10) ─────────────────
// Colors are Unity composite-int RGB — computed web-side as (r<<16)|(g<<8)|b.
// `stagingCache` is '' except on a scene run's first shot: JSON.stringify({fingerprint, relations}).
// `repush` is true only on the LAST send of a batch.
//
// 20260828 - `environmentPresent` is true when a REAL environment (a chosen set, or an AI background)
// was applied to this scene in the SAME Apply — the same category of Unity-side hint `stagingCache` and
// `repush` already are, NOT web-side mutation logic. Unity uses it to skip its blocks delete-then-insert.
// Why it exists: `applySceneSelection`'s 'set' branch runs `applySet` FIRST and rough-blocks SECOND, so by
// the time `applyRoughBlock` lands the shot already holds the imported set's `blockobjects` rows, and
// Unity's unfiltered `DELETE FROM blockobjects WHERE shotid=…` (DatabaseManager.DeleteShotBlockObjects,
// added cs:5427 for the clean-slate coverage path) wiped them — the set landed and vanished inside one
// Apply, leaving only the scene-record floor/skybox the importer adopts, which is exactly the "choosing a
// set only changes the floor" regression. Old bundle + new binary is safe: JsonUtility deserializes an
// absent bool to false, i.e. today's delete-always behaviour.

// 20260831 - AI-LINED COVERAGE: per-shot lining rides the SAME message that creates the shot, so Unity writes
// screenplay_coverage_lines (and the legacy scalar owning shot for `primary` entries) atomically with the
// fresh shot. `coverageLines` is ALWAYS an array — empty means "no lining" — and `coverageColor` is the
// panel's palette default for the index so the scene's colours are pinned from the first write. Old binary
// + new bundle: JsonUtility ignores the unknown keys (lining silently not persisted); new binary + old
// bundle: the absent array deserializes to null and Unity changes nothing scalar-side.
// 20260902 - MERGE APPLY: `mode` absent = today's clean-slate replace (unchanged). `mode: 'merge'`
// updates rows in place keyed by `shotId` (a dto whose `shotId` matches a visible shot in the scene
// updates that shot; any visible shot NOT named by the payload is hidden; a dto with `shotId` 0 —
// or omitted, the JsonUtility default — creates a fresh shot, same as today's create path).
export interface CoverageLinePayload { elementType: 'dialogue' | 'action'; elementId: number; lineStyle: 'straight' | 'wavy'; sortOrder: number; primary: boolean; }
export interface CoverageShotPayload { index: number; shotId?: number; shotSize: string; shotAngle: string; shotMovement: string; focalLength: number; durationSec: number; coverage: string; coverageColor: string; coverageLines: CoverageLinePayload[]; }
export interface ApplyCoveragePayload { sceneId: number; mode?: 'merge'; shots: CoverageShotPayload[]; }
export interface RoughBlockCharacter { characterId: number; castBundledId: string; gender: string; name: string; x: number; y: number; z: number; yrot: number; poseType: string; }
export interface RoughBlockCamera { x: number; y: number; z: number; xrot: number; yrot: number; zrot: number; focalLength: number; }
export interface RoughBlockLight { name: string; lightType: string; lightSubType: string; x: number; y: number; z: number; xrot: number; yrot: number; zrot: number; apertureAngle: number; intensity: number; color: number; shadowStrength: number; }
export interface RoughBlockBlock { name: string; x: number; y: number; z: number; yrot: number; width: number; depth: number; height: number; color: number; }
export interface RoughBlockWall { x: number; y: number; z: number; yrot: number; length: number; height: number; }
export interface RoughBlockObjects { characters: RoughBlockCharacter[]; camera: RoughBlockCamera | null; lights: RoughBlockLight[]; blocks: RoughBlockBlock[]; walls: RoughBlockWall[]; }
export interface ApplyRoughBlockPayload { sceneId: number; shotId: number; repush: boolean; stagingCache: string; environmentPresent: boolean; objects: RoughBlockObjects; rationale: string; }

// ─── Inbound ack waiter: coverageApplied ──────────────────────────────────
// The applyCoverage sequencer (SceneBoardExpanded) arms a fresh Deferred, sends applyCoverage,
// then awaits the ack. Unity emits coverageApplied AFTER it has merged/appended shots and re-pushed
// the project; the handler above resolves the armed Deferred. NB: the ack arrives BEFORE the async
// setSqlitePath→loadProjectFromSqlite store reconcile commits, so phase-2 gating is on the STORE
// (waitForCoverageReconcile), not this ack.

interface Deferred<T> { promise: Promise<T>; resolve: (v: T) => void; reject: (e: unknown) => void; }
function makeDeferred<T>(): Deferred<T> {
  let resolve!: (v: T) => void;
  let reject!: (e: unknown) => void;
  const promise = new Promise<T>((res, rej) => { resolve = res; reject = rej; });
  return { promise, resolve, reject };
}

type CoverageAppliedResult = CoverageAppliedMessage['payload'];
let coverageAppliedWaiter: Deferred<CoverageAppliedResult> | null = null;

/** Arm a fresh pending Deferred; call immediately before sendApplyCoverage. */
export function resetCoverageAppliedWaiter(): void {
  coverageAppliedWaiter = makeDeferred<CoverageAppliedResult>();
}

/** Resolve when the armed waiter receives coverageApplied; reject after timeoutMs (or if not armed). */
export function awaitCoverageApplied(timeoutMs: number): Promise<CoverageAppliedResult> {
  const waiter = coverageAppliedWaiter;
  if (!waiter) return Promise.reject(new Error('[blocking] awaitCoverageApplied called without resetCoverageAppliedWaiter'));
  return new Promise<CoverageAppliedResult>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('[blocking] coverageApplied timed out')), timeoutMs);
    waiter.promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

// ─── Inbound ack waiter: roughBlockApplied ────────────────────────────────
// 20260909 (owner) — BLOCK SHOTS OVERLAY. Same Deferred shape as the coverageApplied waiter above,
// armed once per rough-block RUN rather than per send: `sendApplyRoughBlock` is fire-and-forget and
// only the run's last message carries `repush:true`, which is the one Unity acks. Arm it BEFORE
// applySceneSelection, not after — the background-selection path runs its AI generation after the
// rough block, so the ack can land while that half is still in flight.
//
// 20260909 review fix — the armed state carries the run's `sceneId` alongside its Deferred, because
// "armed before applySceneSelection" is exactly what makes a stale ack dangerous: the window can span
// a whole background generation, a run that blew its cap is never disarmed, and the handler above uses
// this sceneId to tell that run's late ack apart from the one the CURRENT hold is waiting for.

export type RoughBlockAppliedResult = RoughBlockAppliedMessage['payload'];
let roughBlockAppliedWaiter: { deferred: Deferred<RoughBlockAppliedResult>; sceneId: number } | null = null;

/** Arm a fresh pending Deferred for `sceneId`; call immediately before the rough block goes out.
 *  Only an ack naming that scene (or scene 0 — Unity could not parse one) resolves it. */
export function resetRoughBlockAppliedWaiter(sceneId: number): void {
  roughBlockAppliedWaiter = { deferred: makeDeferred<RoughBlockAppliedResult>(), sceneId };
}

/** Resolve when the armed waiter receives roughBlockApplied; reject after timeoutMs (or if not armed). */
export function awaitRoughBlockApplied(timeoutMs: number): Promise<RoughBlockAppliedResult> {
  const waiter = roughBlockAppliedWaiter;
  if (!waiter) return Promise.reject(new Error('[blocking] awaitRoughBlockApplied called without resetRoughBlockAppliedWaiter'));
  return new Promise<RoughBlockAppliedResult>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('[blocking] roughBlockApplied timed out')), timeoutMs);
    waiter.deferred.promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/** Test-only disarm — the waiter is module state, so an armed one leaks across suites (and across
 *  tests in one file, where it decides whether a "nobody armed anything" case is even reachable). */
export function __resetRoughBlockWaiterForTests(): void {
  roughBlockAppliedWaiter = null;
}
